import { Box, CssBaseline, ThemeProvider, createTheme } from '@mui/material'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { AppAlerts } from '../components/AppAlerts/AppAlerts'
import { Sidebar } from '../components/Sidebar/Sidebar'

import './Wrapper.css'
import { OutsideClickDetector } from '../components/OutsideClickDetector/OutsideClickDetector'

interface WrapperProps {
  children?: ReactNode
  onScrollToBottom?: () => void
}

export const Wrapper = ({ children, onScrollToBottom }: WrapperProps) => {

  const theme = createTheme({
    palette: {
      secondary: {
        main: '#d9d9d9'
      },
    }
  })
  const boxRef = useRef<HTMLElement>()

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(false)
  const [backdropMounted, setBackdropMounted] = useState<boolean>(false)

  const onScroll = useCallback(() => {
    if (boxRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = boxRef.current
      const isNearBottom = scrollTop + clientHeight >= scrollHeight
      if (isNearBottom && onScrollToBottom) {
        onScrollToBottom()
      }
    }
  }, [onScrollToBottom])

  useEffect(() => {
    const boxRefEl = boxRef.current

    if (boxRefEl) {
      boxRefEl.addEventListener('scroll', onScroll)

      return () => {
        boxRefEl.removeEventListener('scroll', onScroll)
      }
    }
  }, [onScroll])

  const updateSidebarOpen = (x: boolean) => {
    setSidebarOpen(x)
    if (x) {
      setBackdropMounted(true)
    }
  }

  const handleBackdropTransitionEnd = () => {
    if (!sidebarOpen) {
      setBackdropMounted(false)
    }
  }

  const handleOutsideSidebarClick = () => {
    if (sidebarOpen) {
      updateSidebarOpen(false)
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <Box ref={boxRef} className='wrapper' component='main' sx={{ px: 2, overflowY: 'auto', height: '100vh' }}>
        <CssBaseline />
        <AppAlerts />
        <OutsideClickDetector onOutsideClick={handleOutsideSidebarClick}>
          <Sidebar open={sidebarOpen} setOpen={updateSidebarOpen} />
        </OutsideClickDetector>
        {
          backdropMounted &&
          <div className={`wrapper__backdrop ${sidebarOpen ? 'active' : ''}`} onTransitionEnd={handleBackdropTransitionEnd} />
        }
        <Box sx={{
          display: 'flex',
          height: '100%',
        }}>
          <Box className='wrapper__sidebar-content' sx={{
            width: '100%',
            height: '100%',
            paddingLeft: `calc(${theme.spacing(7)} + 1px)`,
            [theme.breakpoints.up('sm')]: {
              paddingLeft: `calc(${theme.spacing(8)} + 1px)`,
            },
          }}>
            {children}
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  )
}